import { alignment } from "../models/alignment.enum";
import { columnType } from "./models/column-type.enum";
import { headType } from "./models/head-type.enum";
import { HeadType, getHeadTypesModel } from "./models/head-types.model";
import { BaseBabylonService } from "../base-babylon.service";
import { bodiesEnum } from "./models/bodies.enum";
import { PartTransformation } from "../models/part-transformation";
import { BuildingHelperService } from "../../../app-confi/services/building-helper.service";
import { SceneManager } from "../../../common/scene.manager";
import { config } from "../../../app-confi/app-config";
import { accuracyEnum } from "../../../common/models/accuracy.enum";
import { partTypeEnum } from "./models/part-type.enum";
import { CorridorColumn, CorridorPart } from "./models/props-config";
import { segmentType } from "./models/segment-type";
import { buildingType } from "../models/building-type.enum";
import { columnDefs, columnSubtype, subtypeEnum } from "./models/column-definition";
import { Point3 } from "../../../common/models/point.type";

export class CorridorBabylonService extends BaseBabylonService {

    private bhs: BuildingHelperService;

    constructor(
        sceneManager: SceneManager,
        buildingHelperService: BuildingHelperService,
    ) {
        super(sceneManager);
        this.bhs = buildingHelperService;
    }

    isMoosPlazaColumn(type: columnType | headType) {
        return columnDefs.find(c => c.type === type).columnSubtypes.includes(columnSubtype.moosplaza)
    }

    mapPartsTo3D(
        partsIn: CorridorPart[],
        moosPlazaAlignment: alignment,
        moosPlazaPosition: bodiesEnum,
        doNotOrder?: boolean): CorridorPart[] {
        // make copy, dont manipulate existing columns

        if (partsIn && partsIn.length > 0) {
            const floorHeight = this.bhs.floorHeightOfBuilding(buildingType.doubleLoadedCorridor);
            let bodyAColumns = partsIn.find(p => p.type === partTypeEnum.bodyA).columns.map(c => ({ ...c }));
            let bodyBColumns = partsIn.find(p => p.type === partTypeEnum.bodyB).columns.map(c => ({ ...c }));
            let headLeftColumn = partsIn.find(p => p.type === partTypeEnum.headLeft).columns.map(c => ({ ...c }))[0];
            let headRightColumn = partsIn.find(p => p.type === partTypeEnum.headRight).columns.map(c => ({ ...c }))[0];

            // load not in chunks of columns, but first load left head,
            // then columns on body A in a group with one transf,
            // then body B with another global transform,
            // then head right
            let bodyA;
            let bodyB;

            if (doNotOrder) {
                bodyA = bodyAColumns;
                bodyB = bodyBColumns;
            } else {
                // if (selectedHeadType === headType._O_TLH) {
                //     bodyA = this.orderColumns(moosPlazaAlignment, moosPlazaPosition, bodyAColumns);
                //     bodyB = this.orderColumns(moosPlazaAlignment, moosPlazaPosition, bodyBColumns);
                // } else {
                // heads that dont include staircases have to be ordered differently
                bodyA = this.orderColumnsEdges(moosPlazaAlignment, moosPlazaPosition, bodyAColumns);
                bodyB = this.orderColumnsEdges(moosPlazaAlignment, moosPlazaPosition, bodyBColumns);
                // }
            }

            //#region Head Left

            let offsetBasedOnColumn = 0;
            offsetBasedOnColumn = this.getUnitAndAmenityDataForColumn(headLeftColumn, offsetBasedOnColumn, floorHeight);
            const headLeftColumns = headLeftColumn ? [headLeftColumn] : [];
            const headLeftPart: CorridorPart = {
                type: partTypeEnum.headLeft,
                columns: headLeftColumns
            }

            //#endregion

            //#region Body A

            let selectedLeftHeadType = getHeadType(partsIn, partTypeEnum.headLeft);

            let selectedHeadLeftData = getHeadTypesModel().find(s => s.type === selectedLeftHeadType);
            if (!selectedHeadLeftData) {
                selectedHeadLeftData = {
                    orientationConfig: {
                        shift: 0,
                        corridorCenterDistance: 0,
                        corridorWidth: 1.5,
                    },
                    width: 0
                } as any;
            }
            offsetBasedOnColumn = this.getUnitsAndAmenitiesForBodyColumns(offsetBasedOnColumn, selectedHeadLeftData, bodyA);

            const bodyAPart: CorridorPart = {
                type: partTypeEnum.bodyA,
                columns: bodyA
            }

            //#endregion

            //#region Corridor

            offsetBasedOnColumn = selectedHeadLeftData.width;

            //#endregion

            //#region Body B

            offsetBasedOnColumn = this.getUnitsAndAmenitiesForBodyColumns(offsetBasedOnColumn, selectedHeadLeftData, bodyB);

            const bodyBPart: CorridorPart = {
                type: partTypeEnum.bodyB,
                columns: bodyB
            }

            //#endregion

            //#region Head Right

            let selectedRightHeadType = getHeadType(partsIn, partTypeEnum.headRight);
            let selectedHeadRightData = getHeadTypesModel().find(s => s.type === selectedRightHeadType);
            if (!selectedHeadRightData) {
                selectedHeadRightData = {
                    orientationConfig: {
                        shift: 0,
                        corridorCenterDistance: 0,
                        corridorWidth: 1.5,
                    },
                    width: 0
                } as any;
            }

            const bodyAbays = bodyAPart.columns.map(a => a.bays).reduce((p, c) => p + c, 0);
            const bodyBbays = bodyBPart.columns.map(b => b.bays).reduce((p, c) => p + c, 0);
            let longestBays = 0;
            if (bodyAbays >= bodyBbays) {
                longestBays = bodyAbays;
            } else {
                longestBays = bodyBbays;
            }
            const lengthLongest = longestBays * config.corridorBaySize;
            const headLeftWidth = selectedHeadLeftData ? selectedHeadLeftData.width : 0;
            offsetBasedOnColumn = headLeftWidth + lengthLongest - selectedHeadRightData.orientationConfig.shift;
            this.getUnitAndAmenityDataForColumn(headRightColumn, offsetBasedOnColumn, floorHeight);

            const headRightColumns = headRightColumn ? [headRightColumn] : [];
            const headRightPart: CorridorPart = {
                type: partTypeEnum.headRight,
                columns: headRightColumns
            }

            //#endregion

            //#region Corridor
            // get O-G corridor amenities for the center of the building


            const lengthOfCorridor = longestBays * config.corridorBaySize - selectedHeadRightData.orientationConfig.shift;
            const widthOfCorridor = selectedHeadRightData.orientationConfig.corridorWidth;
            const areaPerFloor = lengthOfCorridor * widthOfCorridor;
            let columns = [headLeftPart, bodyAPart, bodyBPart, headRightPart].map(p => p.columns).flat();

            let minFloors = 0;
            columns.forEach(column => {
                let totalFloors = 0;
                column.segments.forEach(s => { totalFloors += s.floorsInSegment });
                if (totalFloors > minFloors) {
                    minFloors = totalFloors;
                }
            })

            const totalAreaCorridor = areaPerFloor * (minFloors);
            const corridor: CorridorColumn = {
                id: Math.random().toString(),
                type: columnType.Corridor,
                unitsAndAmenities: [
                    {
                        amenities: [{
                            GFA: totalAreaCorridor
                        }]
                    }
                ], segments: [
                    {
                        type: segmentType.middle,
                        subtype: subtypeEnum.single,
                        floorsInSegment: minFloors,
                        sectionTypes: []
                    }
                ]
            }

            const corridorPart: CorridorPart = {
                type: partTypeEnum.corridor,
                columns: [corridor]
            }


            const res = [headLeftPart, bodyAPart, bodyBPart, corridorPart, headRightPart];
            return res;
        } else {
            return [];
        }
    }


    renderColumnsTo3D(
        buildingId: string | number,
        parts: CorridorPart[],
        locationInSite,
        accuracy: accuracyEnum
    ): Promise<any> {

        const promises = [];
        const floorHeight = this.bhs.floorHeightOfBuilding(buildingType.doubleLoadedCorridor);

        if (parts.find(p => p.type === partTypeEnum.headLeft)) {

            //#region Head Left


            const headLeftColumn = parts.find(p => p.type === partTypeEnum.headLeft).columns[0];

            let s = {
                orientationConfig: {
                    shift: 0,
                    corridorCenterDistance: 0,
                    corridorWidth: 1.5,
                },
                width: 0,
            } as any;

            if (headLeftColumn) {
                const h = getHeadTypesModel().find(s => s.type === headLeftColumn.type);
                if (h) {
                    s = h;
                }
            } else {
                const headRightColumn = parts.find(p => p.type === partTypeEnum.headRight).columns[0];
                if (headRightColumn) {
                    const h = getHeadTypesModel().find(s => s.type === headRightColumn.type);
                    if (h) {
                        s = h;
                        s.width = 0;
                    }
                }
            }

            let headLeftPartTransformation: PartTransformation = {
                id: 'headLeft',
                position: [0, 0, s.orientationConfig.corridorCenterDistance],
                rotation: [0, 0, 0],
                scale: [1, 1, 1],
            };

            if (headLeftColumn) {
                const headLeftPlinthSegment = this.bhs.getColumnSegment(headLeftColumn, segmentType.plinth);
                if (headLeftPlinthSegment.custom) {
                    const headTypeModel = getHeadTypesModel().find(t => t.type === headLeftColumn.type);

                    const locationInBuilding: Point3 = [
                        headTypeModel.width / 2,
                        headLeftPlinthSegment.customSegmentHeight / 2,
                        -headTypeModel.length / 2,
                    ];
                    this.sceneManager.modelLoaderService.createCustomBoxMesh(
                        buildingId,
                        headTypeModel.width,
                        headTypeModel.length,
                        headLeftPlinthSegment.customSegmentHeight,
                        { groupId: buildingId, subgroupId: headLeftColumn.id, type: headLeftPlinthSegment.sectionTypes[0], props: {} },
                        locationInBuilding,
                        locationInSite,
                        headLeftPartTransformation);
                }

                const renderPromisesHeadLeft = this.renderInstances(buildingId, headLeftColumn.id, headLeftColumn.unitsAndAmenities, locationInSite, headLeftPartTransformation, accuracy, floorHeight);
                promises.push(...renderPromisesHeadLeft);
            }

            //#endregion

            //#region Body A



            let partTransformationA: PartTransformation = {
                id: 'bodyA',
                position: [-s.orientationConfig.shift, 0, - (s.orientationConfig.corridorWidth / 2)],
                rotation: [0, 0, 0],
                scale: [1, 1, 1]
            };

            let offsetBasedOnColumn = s.width;

            const bodyA = parts.find(p => p.type === partTypeEnum.bodyA).columns;
            bodyA.forEach(column => {

                const columnSegment = this.bhs.getColumnSegment(column, segmentType.plinth);
                if (columnSegment.custom) {
                    const width = column.bays * config.corridorBaySize;
                    const locationInBuilding: Point3 = [
                        offsetBasedOnColumn + width / 2,
                        columnSegment.customSegmentHeight / 2,
                        -columnSegment.customSegmentWidth / 2,
                    ];
                    this.sceneManager.modelLoaderService.createCustomBoxMesh(
                        buildingId,
                        width,
                        columnSegment.customSegmentWidth,
                        columnSegment.customSegmentHeight,
                        { groupId: buildingId, subgroupId: column.id, type: columnSegment.sectionTypes[0], props: {} },
                        locationInBuilding,
                        locationInSite,
                        partTransformationA);
                }
                offsetBasedOnColumn = offsetBasedOnColumn + column.bays * config.corridorBaySize;
                const renderPromises = this.renderInstances(buildingId, column.id, column.unitsAndAmenities, locationInSite, partTransformationA, accuracy, floorHeight);
                promises.push(...renderPromises);
            })

            //#endregion

            //#region Corridor
            const bodyB = parts.find(p => p.type === partTypeEnum.bodyB).columns;
            const bays = bodyB.map(col => col.bays).reduce((p, c) => p + c, 0);
            const length = bays * config.corridorBaySize;

            const bodyAbays = bodyA.map(a => a.bays).reduce((p, c) => p + c, 0);
            const bodyBbays = bodyB.map(b => b.bays).reduce((p, c) => p + c, 0);
            let longestBays = 0;
            if (bodyAbays >= bodyBbays) {
                longestBays = bodyAbays;
            } else {
                longestBays = bodyBbays;
            }
            const lengthLongest = longestBays * config.corridorBaySize;

            offsetBasedOnColumn = s.width;


            const lengthOfCorridor = longestBays * config.corridorBaySize - s.orientationConfig.shift;
            const widthOfCorridor = s.orientationConfig.corridorWidth;
            const heightCorridorSlab = 0.3

            let minFloors = 0;
            const columns = parts.map(p => p.columns).flat();
            columns.forEach(column => {
                let totalFloors = 0;
                column.segments.forEach(s => { totalFloors += s.floorsInSegment });
                if (totalFloors > minFloors) {
                    minFloors = totalFloors;
                }
            })

            const locationsInBuilding = [];

            for (let i = 0; i < minFloors; i++) {
                const locationInBuilding = [
                    offsetBasedOnColumn + lengthOfCorridor / 2, (i * floorHeight) - heightCorridorSlab / 2, 0
                ];
                locationsInBuilding.push(locationInBuilding);
            }

            locationsInBuilding.forEach(locationInBuilding => {
                this.sceneManager.modelLoaderService.createCustomBoxMesh(
                    buildingId,
                    lengthOfCorridor,
                    widthOfCorridor,
                    heightCorridorSlab,
                    { groupId: buildingId, subgroupId: 'corridor', type: 'O-G', props: {} },
                    locationInBuilding,
                    locationInSite,
                    null);
            })

            //#endregion

            //#region Body B

            let partTransformationB: PartTransformation = {
                id: 'bodyB',
                position: [length + (offsetBasedOnColumn * 2), 0, s.orientationConfig.corridorWidth / 2],
                rotation: [0, Math.PI, 0],
                scale: [1, 1, 1]
            };

            bodyB.forEach(column => {
                const columnPlinthSegment = this.bhs.getColumnSegment(column, segmentType.plinth);
                if (columnPlinthSegment.custom) {
                    const width = column.bays * config.corridorBaySize;
                    const locationInBuilding: Point3 = [
                        offsetBasedOnColumn + width / 2,
                        columnPlinthSegment.customSegmentHeight / 2,
                        -columnPlinthSegment.customSegmentWidth / 2,
                    ];
                    this.sceneManager.modelLoaderService.createCustomBoxMesh(
                        buildingId,
                        width,
                        columnPlinthSegment.customSegmentWidth,
                        columnPlinthSegment.customSegmentHeight,
                        { groupId: buildingId, subgroupId: column.id, type: columnPlinthSegment.sectionTypes[0], props: {} },
                        locationInBuilding,
                        locationInSite,
                        partTransformationB);
                }
                offsetBasedOnColumn = offsetBasedOnColumn + column.bays * config.corridorBaySize;
                const renderPromises = this.renderInstances(buildingId, column.id, column.unitsAndAmenities, locationInSite, partTransformationB, accuracy, floorHeight);
                promises.push(...renderPromises);
            })

            //#endregion

            //#region Head Right

            let headRightPartTransformation: PartTransformation = {
                id: 'headRight',
                position: [0, 0, s.orientationConfig.corridorCenterDistance],
                rotation: [0, 0, 0],
                scale: [1, 1, 1],
            };

            offsetBasedOnColumn = s.width + lengthLongest - s.orientationConfig.shift;
            const headRight = parts.find(p => p.type === partTypeEnum.headRight).columns[0];

            if (headRight) {
                const headRightPlinthSegment = this.bhs.getColumnSegment(headRight, segmentType.plinth);
                if (headRightPlinthSegment.custom) {
                    const headTypeModel = getHeadTypesModel().find(t => t.type === headRight.type);

                    const locationInBuilding: Point3 = [
                        offsetBasedOnColumn + headTypeModel.width / 2,
                        headRightPlinthSegment.customSegmentHeight / 2,
                        -headTypeModel.length / 2,
                    ];
                    this.sceneManager.modelLoaderService.createCustomBoxMesh(
                        buildingId,
                        headTypeModel.width,
                        headTypeModel.length,
                        headRightPlinthSegment.customSegmentHeight,
                        { groupId: buildingId, subgroupId: headRight.id, type: headRightPlinthSegment.sectionTypes[0], props: {} },
                        locationInBuilding,
                        locationInSite,
                        headRightPartTransformation);
                }

                const renderPromisesHeadRight = this.renderInstances(buildingId, headRight.id, headRight.unitsAndAmenities, locationInSite, headRightPartTransformation, accuracy, floorHeight);
                promises.push(...renderPromisesHeadRight);
            }
            //#endregion
        }
        return Promise.all(promises);
    }

    private getUnitsAndAmenitiesForBodyColumns(
        offsetBasedOnColumn: number,
        s: HeadType, bodyColumns: CorridorColumn[]
    ) {
        offsetBasedOnColumn = s.width;
        const floorHeight = this.bhs.floorHeightOfBuilding(buildingType.doubleLoadedCorridor);

        bodyColumns.forEach((column) => {
            offsetBasedOnColumn = this.getUnitAndAmenityDataForColumn(column, offsetBasedOnColumn, floorHeight);
        });
        return offsetBasedOnColumn;
    }

    private getUnitAndAmenityDataForColumn(column: CorridorColumn, offsetBasedOnColumn: number, floorHeight: number) {
        if (column) {
            column.unitsAndAmenities = [];
            const columnSegments = column.segments;
            let currentSegmentHeight = 0;
            columnSegments.forEach(segment => {
                if (segment.type === segmentType.plinth) {
                    if (!segment.custom) {
                        const unitsAndAmenities = this.getUnitAndAmenityData(segment.sectionTypes[0], offsetBasedOnColumn, 0);
                        column.unitsAndAmenities.push(unitsAndAmenities);
                    }
                } else if (segment.type === segmentType.middle) {
                    for (let f = 0; f < segment.floorsInSegment; f++) {
                        const height = f * floorHeight;
                        const unitsAndAmenities = this.getUnitAndAmenityData(segment.sectionTypes[0], offsetBasedOnColumn, currentSegmentHeight + height);
                        column.unitsAndAmenities.push(unitsAndAmenities);
                    }
                } else if (segment.type === segmentType.crown) {
                    const unitsAndAmenities = this.getUnitAndAmenityData(segment.sectionTypes[0], offsetBasedOnColumn, currentSegmentHeight);
                    column.unitsAndAmenities.push(unitsAndAmenities);
                }
                if (!segment.customSegmentHeight) {
                    currentSegmentHeight += segment.floorsInSegment * floorHeight;
                } else {
                    currentSegmentHeight += segment.customSegmentHeight;
                }
            });
            offsetBasedOnColumn = offsetBasedOnColumn + column.bays * config.corridorBaySize;
        }
        return offsetBasedOnColumn;
    }

    private orderColumnsEdges(moosPlazaAlignment: alignment, moosPlazaPosition: bodiesEnum, columns: CorridorColumn[]) {
        const columnsInOrder: CorridorColumn[] = [];

        // add left head or moos plein column as first thing in the list
        const moosPlaza = columns.find(column => this.isMoosPlazaColumn(column.type));
        const allCoreColumns = columns.filter(c => c.type === columnType.O_TL);

        let first;
        let second;

        if (moosPlazaAlignment === alignment.center) {
            first = allCoreColumns.pop();
            second = allCoreColumns.pop();
        }


        if (moosPlazaAlignment === alignment.left && moosPlazaPosition === bodiesEnum.A) {
            if (moosPlaza) {
                columnsInOrder.push(moosPlaza);
            }
            second = allCoreColumns.pop();
        } else if (moosPlazaAlignment === alignment.right && moosPlazaPosition === bodiesEnum.B) {
            if (moosPlaza) {
                columnsInOrder.push(moosPlaza);
            }
            second = allCoreColumns.pop();
        } else if (moosPlazaAlignment === alignment.left && moosPlazaPosition === bodiesEnum.B) {
            first = allCoreColumns.pop();
        } else if (moosPlazaAlignment === alignment.right && moosPlazaPosition === bodiesEnum.A) {
            first = allCoreColumns.pop();
        }

        if (first) {
            columnsInOrder.push(first);
        }

        // add all in betweens
        const allExceptHeadsStaircasesAndMoosPlein = columns.filter(c => c.type === columnType.C_XS || c.type === columnType.C_M || c.type === columnType.C_S1S2 || c.type === columnType.C_L || c.type === columnType.C_XL);
        const shuffledColumns = this.equalizeColumns(allExceptHeadsStaircasesAndMoosPlein);

        const totalMiddleBays = shuffledColumns.map(s => s.bays).reduce((p, c) => p + c, 0);

        const orderedWithStaircases = this.bhs.orderStaircasesInBody<CorridorColumn>(moosPlazaAlignment, allCoreColumns, totalMiddleBays, shuffledColumns, columns,  this.isMoosPlazaColumn);

        columnsInOrder.push(...orderedWithStaircases);

        // as a last lets get the right column
        if (moosPlaza && moosPlazaAlignment === alignment.right && moosPlazaPosition === bodiesEnum.A) {
            columnsInOrder.push(moosPlaza);
        } else if (moosPlaza && moosPlazaAlignment === alignment.left && moosPlazaPosition === bodiesEnum.B) {
            columnsInOrder.push(moosPlaza);
        }

        if (second) {
            columnsInOrder.push(second);
        }

        columnsInOrder.forEach((s, index) => {
            s.order = index;
            s.unitsAndAmenities = [];
        });

        return columnsInOrder.sort(s => s.order);
    }


    private equalizeColumns(spreadColumns: CorridorColumn[]): CorridorColumn[] {
        const c_xsColumns = spreadColumns.filter(c => c.type === columnType.C_XS);
        const c_mColumns = spreadColumns.filter(c => c.type === columnType.C_M);
        const c_sColumns = spreadColumns.filter(c => c.type === columnType.C_S1S2);
        const c_lColumns = spreadColumns.filter(c => c.type === columnType.C_L);
        const c_xlColumns = spreadColumns.filter(c => c.type === columnType.C_XL);

        const shuffledColumns = [];
        const equalize = () => {
            if (c_xsColumns.length > 0) {
                shuffledColumns.push(c_xsColumns.pop());
            }
            if (c_mColumns.length > 0) {
                shuffledColumns.push(c_mColumns.pop());
            }
            if (c_sColumns.length > 0) {
                shuffledColumns.push(c_sColumns.pop());
            }
            if (c_lColumns.length > 0) {
                shuffledColumns.push(c_lColumns.pop());
            }
            if (c_xlColumns.length > 0) {
                shuffledColumns.push(c_xlColumns.pop());
            }
            if (shuffledColumns.length !== spreadColumns.length) {
                equalize();
            }
        };
        equalize();
        return shuffledColumns;
    }

}

function getHeadType(partsIn: CorridorPart[], headPart: partTypeEnum) {
    let selectedLeftHead = partsIn.find(p => p.type === headPart);
    let selectedLeftHeadType;
    if (selectedLeftHead && selectedLeftHead.columns.length > 0) {
        const headCol = selectedLeftHead.columns[0];
        selectedLeftHeadType = headCol.type;
    }
    return selectedLeftHeadType;
}
